/**
 * opportunityPresentation — pure-logic lift of `AccountDetailPanels.tsx`'s
 * opportunity half (bead terra-design-v2-drilldowns-ppn.6, plan 005 U8b).
 *
 * React-free / Glaze-free by design (coding standards §1, "pure logic
 * first"): `AccountDetailPanels.tsx` imports every one of these back and
 * its JSX/classNames/`data-testid`s are otherwise untouched — this file is
 * an EXTRACTION, not a rewrite. `buildOpportunityActivityStream` is
 * re-exported from `AccountDetailPanels.tsx` because
 * `AccountDetailPanels.test.tsx:21` imports it from there and that suite
 * stays byte-unchanged (plan 005 §0.9).
 */
import type {
  OpportunityPersonas,
  OpportunityActivityCall,
  OpportunityActivityEmail,
} from '@shared/flow/account-view';
import { parseNextStepHistory } from '@shared/flow/next-step-history';

// ── Key Personas (fixed 6-row section, Decision 7) ──────────────────────────

export const PERSONA_ROWS: { key: keyof OpportunityPersonas; label: string }[] = [
  { key: 'champion', label: 'Champion' },
  { key: 'economic_buyer', label: 'Economic Buyer' },
  { key: 'decision_maker', label: 'Decision Maker' },
  { key: 'technical_stakeholder', label: 'Technical Stakeholder' },
  { key: 'budget_holder', label: 'Budget Holder' },
  { key: 'primary_clay_user', label: 'Primary Clay User' },
];

/** "Set" means the role's contact id is populated — independent of whether
 * the batched `SalesforceContact` lookup resolved a display name (an
 * unmatched id still counts; only the LABEL falls back, never the count). */
export function personaSetCount(personas: OpportunityPersonas): number {
  return PERSONA_ROWS.filter(r => personas[r.key] != null).length;
}

/** Primary Clay User is special-cased to the denormalized
 * `primary_clay_user_name`/`_title` display fields rather than the
 * resolved-contact link every other role uses (Approach note, U8) — those
 * two fields are already on the Opportunity object (no join), so they're
 * the more current display source for that one role. `null` when no name
 * is set — the caller renders the "— not set" empty state itself. */
export function primaryClayUserDisplay(name: string | null, title: string | null): string | null {
  return name ? [name, title].filter(Boolean).join(' — ') : null;
}

// ── Close status / stage pill ────────────────────────────────────────────

export type OppCloseStatus = 'open' | 'won' | 'lost' | 'closed';

/**
 * Derives the opp's won/lost/open state from `opp.stage` text before
 * falling back to `opp.is_closed` for the generic open/closed binary.
 * SFDC's own stage-picklist convention already uses the literal values
 * "Closed Won" / "Closed Lost" for a closed deal (see
 * `OpportunityRowSchema.is_closed` comment, `src/shared/flow/account-
 * view.ts`: "closed stage (Closed Won / Closed Lost)"), so a case-
 * insensitive substring check on the stage text disambiguates won vs
 * lost before `is_closed` is ever consulted. `is_closed` only decides
 * open-vs-generic-closed when the stage text itself doesn't say — a
 * fringe case for real SFDC data (every closed stage we've seen says
 * "Won" or "Lost"), kept as a safe fallback rather than assumed away.
 */
export function oppCloseStatus(stage: string | null, isClosed: boolean | null): OppCloseStatus {
  const s = (stage ?? '').toLowerCase();
  if (s.includes('won')) return 'won';
  if (s.includes('lost')) return 'lost';
  return isClosed ? 'closed' : 'open';
}

export const OPP_STATUS_LABEL: Record<OppCloseStatus, string> = { open: 'Open', won: 'Won', lost: 'Lost', closed: 'Closed' };

/** "Closed Won"/"Closed Lost" stage text already unambiguously conveys both
 * closed-ness and win/loss — the stage pill gets colored instead of also
 * rendering a separate status pill (avoids "Closed Won · Closed" double-
 * redundancy, Spencer's screenshot ask). Only an open stage (or the
 * generic-closed fallback) gets its own status pill. */
export function stageConveysCloseStatus(closeStatus: OppCloseStatus): boolean {
  return closeStatus === 'won' || closeStatus === 'lost';
}

// ── Meta line + meta chips ───────────────────────────────────────────────

/** Locale-formatted date matching `AccountDetailPanels.tsx`'s local `fmt` —
 * duplicated here (not imported) because `fmt` also formats plain
 * (non-opportunity) account fields and stays local to that file; this is
 * the one date shape `oppMetaText` needs. */
function fmtOppDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The opp row's meta line — "{type} · {close date}", either half omitted
 * when absent, empty string when both are absent. */
export function oppMetaText(opportunity: { type: string | null; close_date: string | null }): string {
  return [opportunity.type, fmtOppDate(opportunity.close_date)].filter(Boolean).join(' · ');
}

/** One meta-chip's label — "no {emptyLabel}" at zero, "1 {singular}" at
 * one, "{N} {plural}" above one. */
export function metaChipLabel(chip: { count: number; singular: string; plural: string; emptyLabel: string }): string {
  return chip.count ? `${chip.count} ${chip.count === 1 ? chip.singular : chip.plural}` : chip.emptyLabel;
}

// ── Gong deep-link ────────────────────────────────────────────────────────

/** Gong deep-link — kept as a tiny LOCAL helper rather than importing
 * `gongCallUrl` from `manager/dealHealthHelpers.ts`: flow components stay
 * self-contained (same reasoning as `links.tsx`'s header note), and this is
 * the only Gong link the Flow surface renders. */
export function gongCallUrl(callId: string): string {
  // Clay's Gong workspace subdomain (`us-84989`) — confirmed against a real
  // prod link (2026-08-14), matches `manager/dealHealthHelpers.ts`'s copy.
  return `https://us-84989.app.gong.io/call?id=${encodeURIComponent(callId)}`;
}

/** Collapsed Call sub-row's participants line — real names joined with
 * their affiliation when known, or the "nothing recorded" empty state. */
export function callParticipantsText(participants: { party_name: string; affiliation: string | null }[]): string {
  return participants.length
    ? participants.map(p => (p.affiliation ? `${p.party_name} (${p.affiliation})` : p.party_name)).join(', ')
    : 'No participants recorded.';
}

/** Every account call rendered in the activity stream is date-windowed, not
 * deal-attributed — say so once rather than per-row noise (Decision 8). */
export const DEAL_WINDOW_NOTE = 'Account calls in deal window (not deal-attributed)';

// ── Interleaved activity stream (Decision 7) ────────────────────────────

export function toMs(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.getTime();
}

export type OppStreamItem =
  | { kind: 'nsh'; key: string; when: string | null; text: string; sortMs: number | null }
  | { kind: 'call'; key: string; sortMs: number | null; call: OpportunityActivityCall }
  | { kind: 'email'; key: string; sortMs: number | null; email: OpportunityActivityEmail };

/** Cap on the merged activity stream (Decision 7) — the first N items, most
 * recent first; the rest are reachable via the "View more in Salesforce ↗"
 * overflow link. */
export const OPP_ACTIVITY_CAP = 10;

/**
 * Merges parsed Next Step History entries + Calls + Emails into ONE
 * desc-ordered activity stream (Decision 7), capped to the first
 * `OPP_ACTIVITY_CAP` items. Undated items (an NSH raw-fallback entry with
 * no matched timestamp) sort LAST — the same nulls-last convention
 * `account-timeline.ts`'s merge uses for signals/activity — so a single
 * raw-fallback NSH entry still renders, just at the bottom of the stream,
 * as one undated block.
 */
export function buildOpportunityActivityStream(
  nextStepHistoryRaw: string | null,
  calls: OpportunityActivityCall[],
  emails: OpportunityActivityEmail[],
): { items: OppStreamItem[]; overflow: boolean } {
  const nshItems: OppStreamItem[] = parseNextStepHistory(nextStepHistoryRaw).map((entry, i) => ({
    kind: 'nsh',
    key: `nsh-${i}`,
    when: entry.when,
    text: entry.text,
    sortMs: toMs(entry.when),
  }));
  const callItems: OppStreamItem[] = calls.map(c => ({
    kind: 'call',
    key: `call-${c.call_id}`,
    sortMs: toMs(c.started_at),
    call: c,
  }));
  const emailItems: OppStreamItem[] = emails.map((e, i) => ({
    kind: 'email',
    key: `email-${e.source_id}-${i}`,
    sortMs: toMs(e.effective_at),
    email: e,
  }));

  const merged = [...nshItems, ...callItems, ...emailItems];
  merged.sort((a, b) => {
    if (a.sortMs == null && b.sortMs == null) return 0;
    if (a.sortMs == null) return 1;
    if (b.sortMs == null) return -1;
    return b.sortMs - a.sortMs;
  });

  return { items: merged.slice(0, OPP_ACTIVITY_CAP), overflow: merged.length > OPP_ACTIVITY_CAP };
}
